import { useState, useEffect } from 'react'
import { Paper, Stack, Text, Group, Badge, Loader } from '@mantine/core'
import { ResponsiveTitle } from '../ResponsiveTitle/ResponsiveTitle'
import { supabase } from '../../lib/supabase'
import type { DietaryRestrictionsSummary } from '../../lib/types'
import styles from './DietarySummary.module.css'

export function DietarySummary() {
  const [summary, setSummary] = useState<DietaryRestrictionsSummary | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchDietarySummary = async () => {
    try {
      setLoading(true)

      // Fetch all guests with dietary info
      const { data: guests, error } = await supabase
        .from('guests')
        .select('dietary_vegan, dietary_gluten_free, dietary_lactose_free, dietary_allergies, dietary_misc')

      if (error) throw error

      // Count checkboxes
      const veganCount = guests?.filter(g => g.dietary_vegan).length || 0
      const glutenFreeCount = guests?.filter(g => g.dietary_gluten_free).length || 0
      const lactoseFreeCount = guests?.filter(g => g.dietary_lactose_free).length || 0

      // Collect text fields
      const allergies = guests
        ?.map(g => g.dietary_allergies)
        .filter(Boolean) as string[] || []
      
      const misc = guests
        ?.map(g => g.dietary_misc)
        .filter(Boolean) as string[] || []

      setSummary({
        vegan: veganCount,
        gluten_free: glutenFreeCount,
        lactose_free: lactoseFreeCount,
        allergies,
        misc,
      })
    } catch (err) {
      console.error('Error fetching dietary summary:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDietarySummary()
  }, [])

  if (loading) {
    return (
      <Paper p="xl" shadow="sm" className={styles.container}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Lade Ernährungsübersicht...</Text>
        </Stack>
      </Paper>
    )
  }

  if (!summary) {
    return null
  }

  const hasTextRestrictions = summary.allergies.length > 0 || summary.misc.length > 0

  return (
    <Paper p="xl" shadow="sm" className={styles.container}>
      <Stack gap="lg">
        <ResponsiveTitle order={2} className={styles.title}>
          Ernährungsübersicht
        </ResponsiveTitle>

        {/* Checkbox counts */}
        <Group gap="md">
          {summary.vegan > 0 && (
            <Badge size="lg" color="green" variant="light">
              {summary.vegan}x Vegan
            </Badge>
          )}
          {summary.gluten_free > 0 && (
            <Badge size="lg" color="orange" variant="light">
              {summary.gluten_free}x Glutenfrei
            </Badge>
          )}
          {summary.lactose_free > 0 && (
            <Badge size="lg" color="blue" variant="light">
              {summary.lactose_free}x Laktosefrei
            </Badge>
          )}
        </Group>

        {/* Text fields summary - detailed lists */}
        {hasTextRestrictions && (
          <Stack gap="sm">
            {summary.allergies.length > 0 && (
              <div>
                <Text size="sm" fw={600} c="golden" mb="xs">
                  Allergien ({summary.allergies.length}):
                </Text>
                <Stack gap={4}>
                  {summary.allergies.map((allergy, idx) => (
                    <Text key={idx} size="sm" c="dimmed">
                      • {allergy}
                    </Text>
                  ))}
                </Stack>
              </div>
            )}

            {summary.misc.length > 0 && (
              <div>
                <Text size="sm" fw={600} c="golden" mb="xs">
                  Sonstiges ({summary.misc.length}):
                </Text>
                <Stack gap={4}>
                  {summary.misc.map((item, idx) => (
                    <Text key={idx} size="sm" c="dimmed">
                      • {item}
                    </Text>
                  ))}
                </Stack>
              </div>
            )}
          </Stack>
        )}

        {!hasTextRestrictions && summary.vegan === 0 && summary.gluten_free === 0 && summary.lactose_free === 0 && (
          <Text c="dimmed" ta="center">
            Keine besonderen Ernährungsanforderungen angegeben
          </Text>
        )}
      </Stack>
    </Paper>
  )
}
